// SSR-friendly audio player with waveform visualization
import { useRef, useState, useEffect, useMemo } from 'react'
import { useWavesurfer } from '@wavesurfer/react'
import TimelinePlugin from 'wavesurfer.js/dist/plugins/timeline.esm.js'
import HoverPlugin from 'wavesurfer.js/dist/plugins/hover.esm.js'
import { Icon } from './ui/icon'

function formatTime(number: number): string {
	const hours = Math.floor(number / 3600)
	if (hours > 0) {
		const minutes = Math.floor((number % 3600) / 60)
		const seconds = Math.floor(number % 60)
		return `${hours}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
	}
	const minutes = Math.floor(number / 60)
	const seconds = Math.floor(number % 60)
	return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`
}

function formatTimeLong(number: number): string {
	const hours = Math.floor(number / 3600)
	if (hours > 0) {
		return formatTime(number)
	}
	return `00:${formatTime(number)}`
}

export default function SimpleAudioPlayer({
	audioUrl,
}: {
	audioUrl: string
}) {
	const containerRef = useRef(null)
	const hoverPluginRef = useRef<any>(null)
	const [mounted, setMounted] = useState(false)

	// Only mount on client
	useEffect(() => {
		setMounted(true)
	}, [])

	// Always call useMemo (hooks must be called unconditionally)
	const plugins = useMemo(() => {
		if (typeof window === 'undefined' || !mounted) {
			return []
		}
		
		const timelinePlugin = TimelinePlugin.create()
		const hoverPlugin = HoverPlugin.create({
			lineColor: '#ff0000',
			lineWidth: 2,
			labelBackground: '#555',
			labelColor: '#fff',
			labelSize: '11px',
		})

		hoverPluginRef.current = hoverPlugin
		return [timelinePlugin, hoverPlugin]
	}, [mounted])

	const { wavesurfer, isPlaying, currentTime } = useWavesurfer({
		container: containerRef,
		height: 200,
		waveColor: 'rgb(130, 150, 200)',
		progressColor: 'rgb(60, 80, 120)',
		dragToSeek: true,
		url: mounted ? audioUrl : '',
		plugins,
	})

	const onPlayPause = () => {
		wavesurfer && void wavesurfer.playPause()
	}

	useEffect(() => {
		if (!wavesurfer || !hoverPluginRef.current || !mounted) return

		hoverPluginRef.current.on('interaction', () => {
			void wavesurfer.play()
		})
	}, [wavesurfer, mounted])

	return (
		<div className="bg-base-100/80 border-base-300 rounded-2xl border overflow-hidden">
			{/* Hidden audio element with preload for SSR - starts downloading immediately */}
			<audio preload="auto" style={{ display: 'none' }}>
				<source src={audioUrl} type="audio/wav" />
			</audio>

			{/* Waveform visualization */}
			{mounted && (
				<div
					className="border-base-300 overflow-hidden border-b"
					ref={containerRef}
				/>
			)}

			{/* Controls */}
			{mounted && wavesurfer && (
				<div className="bg-base-300/80 grid grid-cols-3 rounded-b-2xl p-2">
					<p className="text-md m-auto font-medium md:text-xl">
						{formatTimeLong(currentTime)}
					</p>
					<div className="m-auto flex">
						<button
							className="btn btn-ghost px-1 sm:px-2"
							onClick={() => {
								if (wavesurfer) {
									wavesurfer.setTime(Math.max(0, currentTime - 10))
								}
							}}
						>
							<Icon name="backward" className="size-5 sm:size-6" />
						</button>
						<button
							onClick={onPlayPause}
							className="btn btn-ghost px-1 sm:px-2"
						>
							{isPlaying ? (
								<Icon name="pause" className="size-5 sm:size-6" />
							) : (
								<Icon name="play" className="size-5 sm:size-6" />
							)}
						</button>
						<button
							className="btn btn-ghost px-1 sm:px-2"
							onClick={() => {
								if (wavesurfer) {
									wavesurfer.setTime(Math.max(0, currentTime + 10))
								}
							}}
						>
							<Icon name="forward" className="size-5 sm:size-6" />
						</button>
					</div>
					<div className="m-auto flex flex-col justify-center text-base">
						<p>{wavesurfer ? formatTime(wavesurfer.getDuration()) : '0:00'}</p>
					</div>
				</div>
			)}
		</div>
	)
}
